import React from "react";
import { Helmet } from "react-helmet";
import { Store } from "../store";
import ReactGA4 from "react-ga4";
import { DateTime } from "luxon";
import { helmetJsonLdProp } from "react-schemaorg";

function truncate(string, num) {
  if (!num) num = 150;
  string = string.replace(/"/, " ");
  if (string.length > num) {
    return string.substring(0, num - 3) + " ...";
  } else return string;
}

export default function Meta(props) {
  const config = Store.useState(s => s.config);
  const { noIndex, item, path, description, brand, updated, fourOFour, image } = props;
  const canonical = path !== undefined ? "https://" + config.domain + path : null;
  const title = props.title ? `${props.title} - ${config.getProperty("information", "shopName")}` : config.getProperty("information", "shopName");
  const defaultImage = config.getProperty("designs", "logoImage");
  const introduction = config.getProperty("information", "introduction");
  const descriptionToUse = description || `${title} - ${introduction}`;

  let indexValue = "index";
  if (noIndex || config.getProperty("hosting", "noindex")) indexValue = "noindex";

  /* eslint-disable-next-line quotes */
  let indexMetaSelector = document.querySelector('meta[name="robots"]');
  if (!indexMetaSelector) {
    indexMetaSelector = document.createElement("meta");
    indexMetaSelector.setAttribute("name", "robots");
    indexMetaSelector = document.getElementsByTagName("head")[0].appendChild(indexMetaSelector);
  }
  indexMetaSelector.setAttribute("content", indexValue);

  if (ReactGA4.isInitialized) ReactGA4.set({ title });

  const jsonLd =
    item?.listings?.map(l =>
      helmetJsonLdProp({
        "@context": "https://schema.org/",
        "@type": "Product",
        name: item.descriptions.main,
        image: props.image || defaultImage,
        description,
        mpn: item.id,
        brand: {
          "@type": "Thing",
          name: props.brand
        },
        offers: {
          "@type": "Offer",
          priceCurrency: config.currency,
          price: l.prices.sale,
          itemCondition: l.secondHand ? "http://schema.org/UsedCondition" : "http://schema.org/NewCondition",
          availability: l.preOrder ? "http://schema.org/PreOrder" : l.stock.quantity ? "http://schema.org/InStock" : "http://schema.org/OutOfStock",
          seller: {
            "@type": "Organization",
            name: config.shopName
          }
        }
      })
    ) || [];

  const listing = item?.listings[0];

  return (
    <Helmet script={[...jsonLd]}>
      <title>{title}</title>
      {canonical ? <link rel="canonical" href={canonical} /> : null}
      {canonical ? <meta property="og:url" content={canonical}></meta> : null}
      <meta name="keywords" content={title.replace(/[^a-zA-Z ]/g, "").replace(/ /g, ",")} />
      <meta property="og:title" content={title} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={config.getProperty("information", "shopName")} />
      {updated ? <meta property="og:updated_time" content={DateTime.fromMillis(updated).toUnix()} /> : null}
      {descriptionToUse ? <meta name="description" content={truncate(descriptionToUse, 150)} /> : null}
      {fourOFour ? <meta name="prerender-status-code" content="404" /> : null}
      {descriptionToUse ? <meta property="og:description" content={truncate(descriptionToUse, 200)} /> : null}
      <meta property="og:image" itemProp="image" content={image || defaultImage} />
      {brand ? <meta property="product:brand" content={brand} /> : null}
      {listing ? <meta property="product:availability" content={listing.stock.quantity > 0 ? "in stock" : "oos"} /> : null}
      {listing ? <meta property="product:condition" content="new" /> : null}
      {listing ? <meta property="product:price:amount" content={listing.prices.sale} /> : null}
      {listing ? <meta property="product:price:currency" content={config.getProperty("information", "currency")} /> : null}
      {props.id ? <meta property="product:retailer_item_id" content={props.id} /> : null}
    </Helmet>
  );
}
